<script setup>
import { computed, ref, watch } from 'vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
const props = defineProps({ usage: { type: Object, default: null } });
const { isAdmin } = useAdmin();
const accountId = useMapGetter('getCurrentAccountId');
const receiptAccountId = ref(null);
watch(
  () => props.usage,
  usage => {
    receiptAccountId.value = usage ? accountId.value : null;
  },
  { immediate: true, flush: 'sync' }
);
watch(
  accountId,
  () => {
    receiptAccountId.value = null;
  },
  { flush: 'sync' }
);
const canViewReceipt = computed(
  () =>
    isAdmin.value &&
    Number(accountId.value) > 0 &&
    receiptAccountId.value === accountId.value
);
const formatNumber = value => Number(value || 0).toLocaleString('pt-BR');
const tokens = computed(() => formatNumber(props.usage?.total_tokens));
const cost = computed(() =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(props.usage?.estimated_cost_brl || 0)
);
const usesCompanyKey = computed(
  () => props.usage?.credential_source === 'account'
);
const sourceLabel = computed(() =>
  usesCompanyKey.value
    ? 'OpenAI da empresa'
    : `${props.usage?.credits || 0} créditos IA utilizados`
);
const detailRows = computed(() => {
  const usage = props.usage || {};
  const rows = [
    { label: 'Entrada', value: `${formatNumber(usage.input_tokens)} tokens` },
    { label: 'Saída', value: `${formatNumber(usage.output_tokens)} tokens` },
  ];
  if (usage.model) rows.push({ label: 'Modelo', value: usage.model });
  if (usage.priced) {
    rows.push(
      { label: 'Em dólares', value: `US$ ${usage.estimated_cost_usd}` },
      { label: 'Cotação', value: `R$ ${usage.usd_brl_rate}` }
    );
  }
  return rows;
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div
    v-if="usage && canViewReceipt"
    class="w-full max-w-sm overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 text-xs text-n-slate-11 dark:border-n-strong"
    role="status"
  >
    <div class="flex items-center gap-2.5 px-3 py-2">
      <span
        class="grid size-7 shrink-0 place-items-center rounded-lg bg-n-brand/10 text-n-brand"
        aria-hidden="true"
      >
        <span class="i-hugeicons-analytics-up size-4" />
      </span>
      <div class="min-w-0 flex-1">
        <p class="m-0 flex flex-wrap items-baseline gap-x-1.5 text-n-slate-12">
          <span
            v-if="usage.token_usage_available"
            class="font-semibold tabular-nums"
          >
            {{ tokens }} tokens
          </span>
          <span v-else>Tokens não informados pelo provedor</span>
          <span class="text-n-slate-9" aria-hidden="true">·</span>
          <span v-if="usage.priced" class="tabular-nums">
            <span class="sr-only">Custo estimado: </span>{{ cost }}
            <span class="text-n-slate-10" aria-hidden="true">estimado</span>
          </span>
          <span v-else>Custo indisponível para esta ação</span>
        </p>
        <p
          v-if="usage.credential_source"
          class="m-0 mt-0.5 flex items-center gap-1 text-n-slate-10"
        >
          <span
            class="size-3.5 shrink-0"
            :class="
              usesCompanyKey ? 'i-hugeicons-key-01' : 'i-hugeicons-coins-01'
            "
            aria-hidden="true"
          />
          <span class="truncate">{{ sourceLabel }}</span>
        </p>
      </div>
    </div>
    <p
      v-if="usage.credits_refunded"
      class="m-0 flex items-center gap-1 border-t border-n-weak px-3 py-1.5 text-n-teal-11"
    >
      <span
        class="i-hugeicons-circle-arrow-reload-01 size-3.5 shrink-0"
        aria-hidden="true"
      />
      {{ usage.credits_refunded }} créditos IA devolvidos.
    </p>
    <details
      v-if="usage.token_usage_available"
      class="group border-t border-n-weak"
    >
      <summary
        class="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-1.5 font-medium text-n-slate-11 transition-colors hover:text-n-slate-12 [&::-webkit-details-marker]:hidden"
      >
        Detalhes do consumo
        <span
          class="i-hugeicons-arrow-down-01 size-4 shrink-0 transition-transform group-open:rotate-180 motion-reduce:transition-none"
          aria-hidden="true"
        />
      </summary>
      <dl class="m-0 grid grid-cols-2 gap-x-3 gap-y-1.5 px-3 pb-2">
        <div v-for="row in detailRows" :key="row.label" class="min-w-0">
          <dt class="text-n-slate-10">{{ row.label }}</dt>
          <dd
            class="m-0 truncate tabular-nums text-n-slate-12"
            :title="row.value"
          >
            {{ row.value }}
          </dd>
        </div>
      </dl>
      <p v-if="usage.priced" class="m-0 px-3 pb-2 text-n-slate-10">
        O custo estimado da OpenAI é diferente do saldo de créditos.
      </p>
    </details>
  </div>
</template>
